import { useMemo, useState, useCallback } from "react";
import { Email } from "@src/types/api";
import {
  ShipmentRowBuilder,
  ShipmentRowVM,
} from "@taskpane/presentation/shipments/ShipmentRowBuilder";
import { ShipmentSelectionState } from "@taskpane/presentation/shipments/ShipmentManager";

export interface UseShipmentsReturn {
  rows: ShipmentRowVM[];
  selectionState: ShipmentSelectionState;
  error: string | null;
  setError: (error: string | null) => void;
  toggleSelection: (row: ShipmentRowVM, selected: boolean) => void;
  clearSelections: () => void;
  canCreateReply: boolean;
  loadingShipments: Set<string>;
  setLoadingShipment: (shipmentId: string, loading: boolean) => void;
}
export function useShipments(
  emailData: Email | null,
  verifiedSender?: boolean
): UseShipmentsReturn {
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingShipments, setLoadingShipments] = useState<Set<string>>(new Set());
  const rows = useMemo(() => {
    return ShipmentRowBuilder.buildAllRows(emailData, verifiedSender);
  }, [emailData, verifiedSender]);
  const selectionState = useMemo(() => {
    const selectedLTLCount = rows.filter(
      (r) => r.mode === "LTL" && selectedIds.has(r.shipmentId)
    ).length;
    const selectedFTLCount = rows.filter(
      (r) => r.mode === "FTL" && selectedIds.has(r.shipmentId)
    ).length;

    return {
      selectedIds,
      selectedLTLCount,
      selectedFTLCount,
    };
  }, [rows, selectedIds]);
  const toggleSelection = useCallback((row: ShipmentRowVM, selected: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);

      if (selected) {
        newSet.add(row.shipmentId);
      } else {
        newSet.delete(row.shipmentId);
      }
      return newSet;
    });
  }, []);
  const setLoadingShipment = useCallback((shipmentId: string, loading: boolean) => {
    setLoadingShipments((prev) => {
      const newSet = new Set(prev);

      if (loading) {
        newSet.add(shipmentId);
      } else {
        newSet.delete(shipmentId);
      }
      return newSet;
    });
  }, []);
  const clearSelections = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  const canCreateReply = useMemo(() => {
    return selectedIds.size > 0;
  }, [selectedIds]);

  return {
    rows,
    selectionState,
    error,
    setError,
    toggleSelection,
    clearSelections,
    canCreateReply,
    loadingShipments,
    setLoadingShipment,
  };
}
